import { useEffect, useMemo, useState } from "react";

import {
  DISPOSAL_RATE_KEYS,
  formatRateEntry,
  planRateSave,
  rateFor,
  rateSaveStatus,
} from "../lib/rateInputs.mjs";
import type { RateRow } from "../lib/rateInputs.d.mts";
import {
  appendRateInputs,
  clearRateInputs,
  fetchRateInputs,
  type RateClear,
  type RateWrite,
} from "../lib/rateInputsClient";
import { useInputActors } from "../lib/inputActors";
import { newestStamp, rateStamp } from "../lib/inputStamps.mjs";
import { domainVar } from "../lib/widgetDomain.mjs";

/**
 * MER-585: Manual Disposal Location Rates.
 *
 * Replaces the localStorage store in Delta's Disposal Tracker v11. Four numbers
 * per disposal location, flat or percentage, exactly the columns operators
 * already maintain.
 *
 * The location list is TYPED, not picked. The DISPOSAL entity is not synced to
 * Supabase (MER-551), so there is no facility list to offer -- and inventing a
 * dropdown from some other table would silently constrain what they can enter.
 * When MER-551 lands this prefills; until then a blocked field would be worse
 * than a free one.
 *
 * Saving a number APPENDS: a change supersedes rather than overwrites. A box
 * blanked (or set to 0) over a saved value CLEARS it, as blanking a box does
 * on the client's page: the stored rows of that key are removed through
 * tenant_rate_input_clear (migration 0100) and a 0 is never stored. Who
 * entered and who cleared what, and when, is kept in report_edit_log.
 *
 * TWO MOUNTS, ONE EDITOR. The staff tab (Cost & Disposal) and Settings >
 * Inputs (the Disposal section, 2026-10-01) both render this component, and
 * the Disposal report's Manual Rates tab plans its Save with the same
 * planRateSave, so the three write the same rows the same way. In Settings
 * the panel also lists every disposal location the tenant's tickets name
 * (knownLocations), is read only for a role that may not write (canEdit, the
 * database's report_edit_can_write), shows who last changed each location's
 * rates and when (the row's own created_by and created_at), and tells its
 * host what a Save changed (onSaved) so the report's cached rates follow.
 */

interface Props {
  tenantId: string | null;
  /** Locations to show. Once MER-551 syncs DISPOSAL this comes from the feed. */
  knownLocations?: readonly string[];
  /**
   * Whether this session may write (report_edit_can_write). Default true: the
   * staff tab's mount, where every reader is staff. False draws the sheet read
   * only and says so.
   */
  canEdit?: boolean;
  /** What a Save wrote and cleared, once it has landed. */
  onSaved?: (writes: RateWrite[], clears: RateClear[]) => void;
}

type Draft = Record<string, Record<string, string>>;

const DOMAIN = "disposal";

export default function RateInputPanel({
  tenantId,
  knownLocations = [],
  canEdit = true,
  onSaved,
}: Props) {
  const [saved, setSaved] = useState<Map<string, RateRow>>(new Map());
  const [draft, setDraft] = useState<Draft>({});
  const [extra, setExtra] = useState<string[]>([]);
  const [newLocation, setNewLocation] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    fetchRateInputs(tenantId)
      .then((m) => {
        if (!cancelled) setSaved(m);
      })
      .catch(() => {
        if (!cancelled) setSaved(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const locations = useMemo(() => {
    const fromSaved = [...saved.values()]
      .filter((r) => r.scope === "facility" && r.scope_key)
      .map((r) => String(r.scope_key));
    return [...new Set([...knownLocations, ...fromSaved, ...extra])].sort();
  }, [knownLocations, saved, extra]);

  // Who last changed a location's rates, and when: the newest of its four
  // rows. Only inside Settings > Inputs, which provides the names.
  const actors = useInputActors();
  const wantActors = actors?.want;
  const stamps = useMemo(() => {
    const byLoc = new Map<string, ReturnType<typeof rateStamp>>();
    for (const loc of locations) {
      byLoc.set(
        loc,
        newestStamp(DISPOSAL_RATE_KEYS.map((c) => rateStamp(rateFor(saved, loc, c.key)))),
      );
    }
    return byLoc;
  }, [locations, saved]);
  useEffect(() => {
    wantActors?.([...stamps.values()]);
  }, [wantActors, stamps]);

  const boxValue = (loc: string, key: string) => {
    const pending = draft[loc]?.[key];
    if (pending !== undefined) return pending;
    const row = rateFor(saved, loc, key);
    if (!row) return "";
    return formatRateEntry({ value: Number(row.rate_value), unit: String(row.rate_unit) });
  };

  const setBox = (loc: string, key: string, v: string) =>
    setDraft((d) => ({ ...d, [loc]: { ...(d[loc] ?? {}), [key]: v } }));

  async function save() {
    if (!tenantId) return;
    if (!canEdit) return;
    const plan = planRateSave(draft, (loc, key) => {
      const row = rateFor(saved, loc, key);
      return row ? { value: Number(row.rate_value), unit: String(row.rate_unit) } : null;
    });
    if (plan.writes.length === 0 && plan.clears.length === 0) {
      setStatus(rateSaveStatus({ invalid: plan.invalid.length }));
      return;
    }
    setBusy(true);
    const wrote = await appendRateInputs(tenantId, plan.writes);
    if (!wrote.ok) {
      setBusy(false);
      setStatus(`Could not save: ${wrote.error ?? "unknown error"}`);
      return;
    }
    const res = await clearRateInputs(tenantId, plan.clears);
    setStatus(
      rateSaveStatus({
        saved: plan.writes.length,
        cleared: res.cleared.length,
        notCleared: plan.clears.length - res.cleared.length,
        invalid: plan.invalid.length,
        error: res.error,
      }),
    );
    if (plan.writes.length > 0 || res.cleared.length > 0) onSaved?.(plan.writes, res.cleared);
    // A box that was not applied (not a number, or a clear that failed) keeps
    // what was typed; every other box shows what is stored, once it is re-read.
    const kept: Draft = {};
    for (const u of [...plan.invalid, ...plan.clears.filter((c) => !res.cleared.includes(c))]) {
      kept[u.scopeKey] = { ...(kept[u.scopeKey] ?? {}), [u.rateKey]: draft[u.scopeKey][u.rateKey] };
    }
    // The sheet stays locked until the stored values are back: a box typed
    // into while they were on their way would be overwritten by them.
    const refreshed = await fetchRateInputs(tenantId);
    setSaved(refreshed);
    setDraft(kept);
    setBusy(false);
  }

  const cell: React.CSSProperties = {
    padding: "8px 10px",
    borderBottom: "0.5px solid var(--border)",
    fontSize: 12,
  };

  return (
    <section
      style={{
        background: "var(--panel)",
        border: "0.5px solid var(--border)",
        borderTop: `3px solid ${domainVar(DOMAIN)}`,
        borderRadius: 6,
        boxShadow: "var(--shadow-tile)",
        overflow: "hidden",
      }}
    >
      <header style={{ padding: "14px 16px 10px" }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
          Manual Disposal Location Rates
        </h3>
        <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--text-muted)", lineHeight: 1.4 }}>
          Flat amounts like <b>40</b>, or percentage add-ons like <b>5%</b>. A percentage applies to
          the disposal subtotal (rate × tons). Clear a box, or enter 0, and save to take a rate off.
          Every entry and every clear is kept in the edit log.
        </p>
      </header>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
          <thead>
            <tr>
              <th
                style={{
                  ...cell,
                  textAlign: "left",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--text-2)",
                }}
              >
                Disposal Location
              </th>
              {DISPOSAL_RATE_KEYS.map((c) => (
                <th
                  key={c.key}
                  style={{
                    ...cell,
                    textAlign: "right",
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: "var(--text-2)",
                  }}
                >
                  {c.label}
                </th>
              ))}
              {actors ? (
                <th
                  style={{
                    ...cell,
                    textAlign: "left",
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: "var(--text-2)",
                  }}
                >
                  Last changed
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 ? (
              <tr>
                <td style={{ ...cell, color: "var(--text-muted)" }} colSpan={actors ? 6 : 5}>
                  {canEdit ? "No locations yet. Add one below." : "No locations yet."}
                </td>
              </tr>
            ) : (
              locations.map((loc) => (
                <tr key={loc}>
                  <td style={{ ...cell, fontWeight: 600 }}>{loc}</td>
                  {DISPOSAL_RATE_KEYS.map((c) => (
                    <td key={c.key} style={{ ...cell, textAlign: "right" }}>
                      <input
                        aria-label={`${loc} ${c.label}`}
                        value={boxValue(loc, c.key)}
                        readOnly={!canEdit || busy}
                        onChange={(e) => setBox(loc, c.key, e.target.value)}
                        placeholder="—"
                        style={{
                          width: 92,
                          textAlign: "right",
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          padding: "4px 6px",
                          border: "0.5px solid var(--border)",
                          borderRadius: 3,
                          // Read only for the role: drawn as Settings > Inputs
                          // draws every other read-only box.
                          background: canEdit ? "var(--panel)" : "var(--bg)",
                          color: canEdit ? "var(--text)" : "var(--text-2)",
                        }}
                      />
                    </td>
                  ))}
                  {actors ? (
                    <td style={{ ...cell, color: "var(--text-2)", whiteSpace: "nowrap" }}>
                      {actors.label(stamps.get(loc)) ?? ""}
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          padding: "12px 16px",
          flexWrap: "wrap",
        }}
      >
        {canEdit ? (
          <>
            <input
              aria-label="New disposal location"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              placeholder="Add a disposal location"
              style={{
                flex: "1 1 220px",
                minWidth: 0,
                fontSize: 12,
                padding: "6px 8px",
                border: "0.5px solid var(--border)",
                borderRadius: 3,
                background: "var(--panel)",
                color: "var(--text)",
              }}
            />
            <button
              type="button"
              onClick={() => {
                const name = newLocation.trim();
                if (!name || locations.includes(name)) return;
                setExtra((x) => [...x, name]);
                setNewLocation("");
              }}
              style={btn(false)}
            >
              Add location
            </button>
            <button type="button" onClick={save} disabled={busy || !tenantId} style={btn(true)}>
              {busy ? "Saving…" : "Save rates"}
            </button>
          </>
        ) : (
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
            Rates are read only for your role.
          </span>
        )}
        {status ? (
          <span style={{ fontSize: 11, color: "var(--text-muted)" }} role="status">
            {status}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function btn(primary: boolean): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: 4,
    cursor: "pointer",
    border: `0.5px solid ${primary ? "transparent" : "var(--border)"}`,
    background: primary ? domainVar(DOMAIN) : "var(--panel)",
    color: primary ? "#ffffff" : "var(--text)",
  };
}
